import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";

export function CreateUserDialog({ open, onClose, onSubmit, text }) {
  const t = text.dialogs.create;
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    group: "",
    active: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        email: "",
        password: "",
        role: "user",
        group: "",
        active: true,
      });
      setSubmitting(false);
    }
  }, [open]);

  const handleChange = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
        active: form.active,
        group: form.group.trim() || null,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const valid = form.name.trim() && form.email.trim() && form.password;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t.name}
            value={form.name}
            onChange={handleChange("name")}
            size="small"
            fullWidth
          />
          <TextField
            label={t.email}
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            size="small"
            fullWidth
          />
          <TextField
            label={t.password}
            type="password"
            value={form.password}
            onChange={handleChange("password")}
            size="small"
            fullWidth
          />
          <TextField
            label={t.role}
            select
            value={form.role}
            onChange={handleChange("role")}
            size="small"
            fullWidth
          >
            <MenuItem value="user">{text.roles.user}</MenuItem>
            <MenuItem value="admin">{text.roles.admin}</MenuItem>
          </TextField>
          <TextField
            label={t.group}
            value={form.group}
            onChange={handleChange("group")}
            size="small"
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(_, v) => setForm((p) => ({ ...p, active: v }))}
              />
            }
            label={t.active}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!valid || submitting}
        >
          {t.create}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function EditUserDialog({ open, onClose, onSubmit, text, user, isSelf }) {
  const t = text.dialogs.edit;
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        role: user.role || "user",
        group: user.group || "",
        active: user.active !== false,
      });
      setSubmitting(false);
    }
  }, [open, user]);

  if (!form) return null;

  const handleChange = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        group: form.group.trim() || null,
        active: form.active,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t.name}
            value={form.name}
            onChange={handleChange("name")}
            size="small"
            fullWidth
          />
          <TextField
            label={t.email}
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            size="small"
            fullWidth
          />
          <TextField
            label={t.role}
            select
            value={form.role}
            onChange={handleChange("role")}
            size="small"
            fullWidth
            disabled={isSelf}
            helperText={isSelf ? " " : undefined}
          >
            <MenuItem value="user">{text.roles.user}</MenuItem>
            <MenuItem value="admin">{text.roles.admin}</MenuItem>
          </TextField>
          <TextField
            label={t.group}
            value={form.group}
            onChange={handleChange("group")}
            size="small"
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(_, v) => setForm((p) => ({ ...p, active: v }))}
                disabled={isSelf}
              />
            }
            label={t.active}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ResetPasswordDialog({ open, onClose, onSubmit, text, user }) {
  const t = text.dialogs.resetPassword;
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword("");
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(password);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t.title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {user?.email ? `${t.message} (${user.email})` : t.message}
        </DialogContentText>
        <TextField
          label={t.newPassword}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          size="small"
          fullWidth
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={!password || submitting}
        >
          {t.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
